import { useEffect, useState } from 'react';

import { Box, Loader2, Sparkles } from 'lucide-react';

import { MockLuminaire } from '../../../types/analytics';
import { fetchLdcAndBuildWireframeWithGrid } from '../../../util/ldc';
import { IesGrid, IesScene, LdcWireframe } from './IesScene';

interface ScenePreviewProps {
  primary: MockLuminaire | null;
  secondary: MockLuminaire | null;
}

const cctToColor = (cct: number): string => {
  if (cct <= 3000) return '#FFB770';
  if (cct <= 3500) return '#FFD3A0';
  if (cct <= 4200) return '#FFF1D6';
  if (cct <= 5000) return '#F4F6FF';
  return '#D6E0FF';
};

type GridState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; grid: IesGrid }
  | { status: 'missing' }
  | { status: 'error' };

const useIesGrid = (url?: string): GridState => {
  const [state, setState] = useState<GridState>({ status: 'idle' });

  useEffect(() => {
    if (!url) {
      setState({ status: 'missing' });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    fetchLdcAndBuildWireframeWithGrid(url)
      .then(({ grid }) => {
        if (!cancelled) setState({ status: 'ready', grid });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  return state;
};

const LampScene = ({ luminaire, role }: { luminaire: MockLuminaire | null; role: 'A' | 'B' }) => {
  const grid = useIesGrid(luminaire?.iesUrl);

  if (!luminaire)
    return (
      <div className="flex-1 rounded-xl border border-dashed border-greyP4 bg-blackP1/40 flex flex-col items-center justify-center p-6 text-center min-h-[420px]">
        <Box size={32} className="text-greyP3 mb-2" />
        <p className="text-greyP2 text-sm">Pick a luminaire to preview Slot {role}</p>
      </div>
    );

  const cctColor = cctToColor(luminaire.colorTemperature);

  return (
    <div className="flex-1 rounded-xl border border-greyP4 bg-black overflow-hidden flex flex-col min-h-[480px]">
      {/* ── 3D scene region ─────────────────────────────────────────── */}
      <div className="relative flex-1 min-h-[300px]">
        <div className="absolute top-3 left-3 z-10">
          {role === 'A' ? (
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-green01 text-black">
              Slot A
            </span>
          ) : (
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border-2 border-green01 text-green01 bg-black/40">
              Slot B
            </span>
          )}
        </div>

        <div className="absolute top-3 right-3 z-10 text-right">
          <div className="text-[10px] text-greyP3 uppercase tracking-wider">CCT</div>
          <div className="text-sm font-bold text-white">{luminaire.colorTemperature}K</div>
        </div>

        {grid.status === 'ready' ? (
          <IesScene grid={grid.grid} color={cctColor} />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            {grid.status === 'loading' ? (
              <>
                <Loader2 size={26} className="text-greyP3 animate-spin mb-2" />
                <p className="text-greyP3 text-xs">Loading photometric data…</p>
              </>
            ) : grid.status === 'error' ? (
              <>
                <Box size={26} className="text-greyP3 mb-2" />
                <p className="text-greyP3 text-xs">Failed to load the IES file.</p>
              </>
            ) : (
              <>
                <Box size={26} className="text-greyP3 mb-2" />
                <p className="text-greyP3 text-xs">No photometric (IES) file available for this luminaire yet.</p>
              </>
            )}
          </div>
        )}

        <div className="absolute bottom-0 left-0 right-0 bg-black/70 backdrop-blur-sm border-t border-greyP4 px-4 py-3 z-10 pointer-events-none">
          <div className="text-white text-sm font-semibold truncate">{luminaire.name}</div>
          <div className="text-greyP2 text-xs flex flex-wrap gap-x-3 mt-1">
            <span>{luminaire.power}W</span>
            <span>·</span>
            <span>{Math.round(luminaire.lumenOutput).toLocaleString()} lm</span>
            <span>·</span>
            <span>{(luminaire.lumenOutput / luminaire.power).toFixed(0)} lm/W</span>
            <span>·</span>
            <span>{luminaire.opticCode}</span>
          </div>
        </div>
      </div>

      {/* ── LDC wireframe strip below the scene ─────────────────────── */}
      {grid.status === 'ready' && (
        <div className="h-[130px] border-t border-greyP4 bg-blackP1/60 relative flex-shrink-0">
          <span className="absolute top-1.5 left-2 z-10 text-[9px] uppercase tracking-wider text-greyP3 font-semibold pointer-events-none">
            LDC · light distribution
          </span>
          <LdcWireframe grid={grid.grid} color={cctColor} />
        </div>
      )}
    </div>
  );
};

export const ScenePreview = ({ primary, secondary }: ScenePreviewProps) => (
  <div className="flex flex-col gap-4">
    <div className="flex flex-col lg:flex-row gap-4">
      <LampScene luminaire={primary} role="A" />
      <LampScene luminaire={secondary} role="B" />
    </div>
    <div className="rounded-lg border border-green04/30 bg-green04/5 px-4 py-3 flex items-start gap-2">
      <Sparkles size={14} className="text-green01 mt-0.5 flex-shrink-0" />
      <p className="text-xs text-greyP2 leading-relaxed">
        Pole-mounted luminaire in open space showing its photometric distribution (candela body) and the resulting
        ground illuminance footprint with isolux contour lines in lx, computed from the IES file. The LDC strip below
        each scene shows the bare light-distribution curve. Drag to orbit, scroll to zoom.
      </p>
    </div>
  </div>
);
